import { FC, useMemo, useCallback } from 'react'
import styles from 'components/features/socialFeed/ComposeTagsSuggests/ComposeTagsSuggests.styl'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { ComposeId, SuggestTag } from 'interfaces/feed'
import { suggestedTagsDataSelector, suggestedTagsStartsFromSelector } from 'selectors/feedSelectors'
import { ActionMenuItem } from 'components/features/pfm/ActionMenu/ActionMenuItem'
import { ActionMenu } from 'components/features/pfm/ActionMenu/ActionMenu'
import cn from 'classnames'
import { composeExecCommandActionCreator } from 'actions/feedComposeActions'
import { useSuggestActiveElement } from 'helpers/composeHooks'
import { Decoration } from 'helpers/composeHelpers'
import { createPluralFormatter } from 'helpers/formatHelpers'

type ComposeTagsSuggestsProps = {
  composeId: ComposeId
  search: string
  decoration: Decoration
}

const getPluralPosts = createPluralFormatter('post', 'posts')

export const ComposeTagsSuggests: FC<ComposeTagsSuggestsProps> = (props) => {
  const { search, composeId, decoration } = props

  const dispatch = useStoreDispatch()

  const isLoading = useStoreSelector((state) => suggestedTagsDataSelector(state).isLoading)
  const suggests = useStoreSelector((state) => suggestedTagsStartsFromSelector(state, search))

  const handleSelect = useCallback<(tag: SuggestTag) => void>((tag) => {
    dispatch(composeExecCommandActionCreator({
      composeId,
      command: {
        type: 'replaceText',
        text: `#${ tag.name } `,
        start: decoration.start,
        end: decoration.end,
      },
    }))
  }, [dispatch, decoration, composeId])
  const [activeIndex, setActiveIndex] = useSuggestActiveElement(suggests, props, handleSelect)

  const children = useMemo(() => {
    const items = suggests.map((tag, index) => (
      <ActionMenuItem
        key={ tag.name }
        isHighlighted={ index === activeIndex }
        fullWidth
        preventHover
        onClick={ () => handleSelect(tag) }
        onMouseMove={ () => setActiveIndex(index) }
        onMouseEnter={ () => setActiveIndex(index) }
      >
        <div className={ cn(styles.item, index === activeIndex && styles.active) }>
          #{ tag.name }
          { !!tag.posts_count && (
            <span className={ styles.count }>
              { tag.posts_count } { getPluralPosts(tag.posts_count) }
            </span>
          )}
        </div>
      </ActionMenuItem>
    ))

    if (isLoading) {
      items.push(
        <ActionMenuItem
          key='loading'
          isDisabled
        >
          Loading...
        </ActionMenuItem>
      )
    }

    return items
  }, [suggests, isLoading, activeIndex, handleSelect, setActiveIndex])

  if (!isLoading && !suggests.length) {
    return null
  }

  return (
    <ActionMenu onHide={ () => { /* do nothing */ } }>
      { children }
    </ActionMenu>
  )
}
